import { useEffect, useRef, useState } from 'react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import type { ActivityTabData } from './ActivityTab';
import type { AttendeeEntry } from './AttendeesPicker';
import type { MeetingProvider } from './MeetingProviderSelect';
import { useDrawerData, type DrawerData } from './useDrawerData';
import {
  composeInitialActivityStart,
  defaultStartDateTime,
  leggiPreferenzaDescrizione,
  resolveIncludeDescriptionInit,
  ricordaPreferenzaDescrizione,
} from './drawerShellHelpers';
import { resolveSyncSwitch } from './syncSwitch';
import type { ActivityNoteDrawerProps, EntityAdapter } from './types';
import { useBookingRule } from '../../../lib/calendar/useBookingRule';
import { BOOKING_PROBE_TYPE, initialActivityType } from '../../../lib/calendar/booking';

/** Ri-esportate dai puri (`drawerShellHelpers.ts`) per i consumer dello stato del form. */
export { defaultStartDateTime, hasNoteContent } from './drawerShellHelpers';

const DB_VALID_TYPES = ['call', 'videochiamata', 'appuntamento', 'sopralluogo', 'email', 'task', 'note', 'other'];

/** Snapshot delle props di contesto, catturato sul RISING EDGE di `isOpen`. */
export interface DrawerSession {
  seq: number;
  editingActivity: any | null;
  initialActivityData: ActivityNoteDrawerProps['initialActivityData'];
  /** Pre-fill dell'editor per il tab Nota (composer inline) — stesso canale del prefill AI. */
  initialNoteText: string | null;
  defaultTab: 'note' | 'activity';
}

/**
 * Stato del form del drawer condiviso: snapshot delle props di contesto sul rising edge di
 * `isOpen`, UNICA istanza TipTap, inizializzazione dei campi attività e default derivati dai
 * dati caricati (`useDrawerData`). Estratto dalla shell (`ActivityNoteDrawer.tsx`) per il
 * budget di righe del task: nessuna query e nessuna scrittura vivono qui.
 *
 * Fonte: LeadStatusUpdateDrawer.tsx:286-305 (editor), :762-889 (reset del form),
 * :892-911 (contenuto dell'editor), :510-516 (provider meeting di default),
 * :877-884 (assegnatario = utente corrente quando non proposto).
 */
export function useDrawerForm(params: {
  isOpen: boolean;
  adapter: EntityAdapter;
  editingActivity?: any;
  initialActivityData: ActivityNoteDrawerProps['initialActivityData'];
  initialNoteText?: string | null;
  defaultTab: 'note' | 'activity';
}) {
  const { isOpen, adapter, editingActivity, initialActivityData, initialNoteText, defaultTab } = params;

  // === Snapshot delle props di contesto sul RISING EDGE di isOpen ===
  // Le props di contesto NON devono resettare il form mentre il drawer è aperto (un cambio
  // di identità di `editingActivity`/`initialActivityData` a metà digitazione azzererebbe
  // nota e campi): vengono congelate all'apertura e lette solo da lì.
  const [session, setSession] = useState<DrawerSession | null>(null);
  const wasOpenRef = useRef(false);
  const seqRef = useRef(0);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      wasOpenRef.current = true;
      seqRef.current += 1;
      setSession({
        seq: seqRef.current,
        editingActivity: editingActivity ?? null,
        initialActivityData: initialActivityData ?? null,
        initialNoteText: initialNoteText ?? null,
        defaultTab,
      });
    } else if (!isOpen && wasOpenRef.current) {
      wasOpenRef.current = false;
    }
    // `requireNote` NON è snapshottato: governa guardie di submit/chiusura, non
    // l'inizializzazione del form, e la fonte lo legge sempre nel valore corrente.
  }, [isOpen, editingActivity, initialActivityData, initialNoteText, defaultTab]);

  const [tab, setTab] = useState<'note' | 'activity'>(defaultTab);
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [titleError, setTitleError] = useState(false);
  const [activityData, setActivityData] = useState<ActivityTabData>(() => ({
    title: '',
    type: 'task',
    status: 'pending',
    startDateTime: defaultStartDateTime(),
    endDateTime: null,
    assigned_to: null,
  }));
  // Regola "Fissare gli appuntamenti" (Impostazioni › Calendario) per chi compila: null = nessuna, come prima
  const booking = useBookingRule(isOpen ? activityData.type : null, adapter.leadOwnerId);
  // Chi compila ha una regola per gli appuntamenti? (serve al tipo di partenza dalla scheda lead)
  const bookingProbe = useBookingRule(isOpen && adapter.kind === 'lead' ? BOOKING_PROBE_TYPE : null, adapter.leadOwnerId);
  const [syncOptIn, setSyncOptIn] = useState(false);
  const [meetingProvider, setMeetingProvider] = useState<MeetingProvider>('none');
  const [selectedAttendees, setSelectedAttendees] = useState<AttendeeEntry[]>([]);
  const [organizerId, setOrganizerId] = useState('');
  const [selectedCalendarId, setSelectedCalendarId] = useState('');
  const [includeDescriptionInEvent, setIncludeDescriptionInEventRaw] = useState(false);
  // known=false: attività storica senza flag persistito — il sync non tocca la
  // description dell'evento. Toccare la spunta rende lo stato noto.
  const [includeDescriptionKnown, setIncludeDescriptionKnown] = useState(true);
  const setIncludeDescriptionInEvent = (v: boolean) => {
    setIncludeDescriptionInEventRaw(v);
    setIncludeDescriptionKnown(true);
    ricordaPreferenzaDescrizione(v);
  };

  const data: DrawerData = useDrawerData({
    isOpen,
    clientId: adapter.clientId,
    assigneeId: activityData.assigned_to,
    activityType: activityData.type,
    editingGoogleEventId: session?.editingActivity?.google_event_id ?? null,
    editingCalendarId: session?.editingActivity?.calendar_id ?? null,
  });

  // === UNICA istanza TipTap: passata a NoteTab e usata come `description` dell'attività ===
  // Placeholder dinamico per tab (M6): la stringa viene letta da un ref al momento del
  // calcolo delle decorations di TipTap, MAI congelata su `defaultTab` come prima (il
  // placeholder restava "Aggiungi una nota..." anche passando al tab Attività).
  const placeholderRef = useRef(defaultTab === 'note' ? 'Aggiungi una nota...' : 'Descrizione attività...');
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-blue-400 hover:text-blue-300 underline' } }),
      Placeholder.configure({ placeholder: () => placeholderRef.current }),
    ],
    content: '',
    onUpdate: ({ editor: instance }) => setNote(instance.getHTML()),
  });

  // Le decorations di TipTap si ricalcolano solo dopo un dispatch: il solo cambio di
  // `tab` (stato React) non lo genera. Aggiorniamo il ref e forziamo una transazione
  // vuota (nessuno step, `addToHistory: false`) — puramente presentazionale, non tocca
  // contenuto/selezione né la logica di `setContent`/`setNote`.
  useEffect(() => {
    placeholderRef.current = tab === 'note' ? 'Aggiungi una nota...' : 'Descrizione attività...';
    if (!editor) return;
    editor.view.dispatch(editor.state.tr.setMeta('addToHistory', false));
  }, [tab, editor]);

  // Tipo messo dal reset di una nuova attivita' senza prefill (null = modifica o prefill: non si tocca)
  const resetTypeRef = useRef<{ seq: number; type: string } | null>(null);

  // === Reset del form all'apertura (fonte :762-889) — `editor` FUORI dalle deps ===
  useEffect(() => {
    if (!session) return;
    setTab(session.defaultTab);
    setNote('');
    setFiles([]);
    setTitleError(false);
    const descInit = resolveIncludeDescriptionInit(session.editingActivity, leggiPreferenzaDescrizione());
    setIncludeDescriptionInEventRaw(descInit.value);
    setIncludeDescriptionKnown(descInit.known);
    setSelectedAttendees([]);

    resetTypeRef.current = null;
    const editing = session.editingActivity;
    if (editing && session.defaultTab === 'activity') {
      setActivityData({
        title: editing.title || '',
        type: editing.type || 'task',
        status: editing.status || 'pending',
        startDateTime: editing.due_date ? new Date(editing.due_date) : defaultStartDateTime(),
        endDateTime: editing.end_date ? new Date(editing.end_date) : null,
        assigned_to: editing.assigned_to || adapter.defaultAssigneeId || null,
        // Esito della riga IN MODIFICA (mai quello di una task aperta prima).
        outcome: editing.outcome || '',
      });
      return;
    }
    if (session.initialActivityData && session.defaultTab === 'activity') {
      // `due_time` opzionale: assente (prefill AI) → 09:00 come da sempre; presente
      // (escalation dal composer inline) → l'ora scelta dall'utente non va persa.
      const start =
        composeInitialActivityStart(session.initialActivityData.due_date, session.initialActivityData.due_time) ??
        defaultStartDateTime();
      setActivityData({
        title: session.initialActivityData.title || '',
        type: session.initialActivityData.type || 'task',
        status: 'pending',
        startDateTime: start,
        endDateTime: new Date(start.getTime() + 15 * 60 * 1000),
        assigned_to: adapter.defaultAssigneeId || null,
        outcome: '',
      });
      return;
    }
    const effectiveDefault = data.activityDefaultType === 'meeting' ? 'videochiamata' : data.activityDefaultType;
    const start = defaultStartDateTime();
    const resetType = DB_VALID_TYPES.includes(effectiveDefault) ? effectiveDefault : 'task';
    resetTypeRef.current = { seq: session.seq, type: resetType };
    setActivityData({
      title: '',
      type: resetType,
      status: 'pending',
      startDateTime: start,
      endDateTime: new Date(start.getTime() + 15 * 60 * 1000),
      assigned_to: adapter.defaultAssigneeId || null,
      outcome: '',
    });
    // `adapter`/`data.*` sono letti volutamente SOLO al rising edge (snapshot, vedi sopra).
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  // === Contenuto dell'editor: effect DEDICATO, keyed sull'attività in editing (fonte :892-911) ===
  // `setContent` NON emette `onUpdate` (default TipTap): senza la doppia scrittura
  // `setContent` + `setNote` il salvataggio scriverebbe una description vuota e la nota
  // "sparirebbe" — regressione storica, non rimuovere.
  useEffect(() => {
    if (!session || !editor) return;
    const editing = session.editingActivity;
    if (editing && session.defaultTab === 'activity' && editing.description) {
      editor.commands.setContent(editing.description);
      setNote(editing.description);
    } else if (session.initialActivityData?.description && session.defaultTab === 'activity') {
      editor.commands.setContent(session.initialActivityData.description);
      setNote(session.initialActivityData.description);
    } else if (session.initialNoteText && session.defaultTab === 'note') {
      // Escalation dal composer inline ("Apri nel drawer completo"): stesso ramo, stesso
      // momento e stessa doppia scrittura `setContent` + `setNote` del prefill AI — nessun
      // secondo canale di popolamento dell'editor UNICO.
      editor.commands.setContent(session.initialNoteText);
      setNote(session.initialNoteText);
    } else {
      editor.commands.setContent('');
    }
  }, [editor, session?.seq, session?.editingActivity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tipo di partenza "Videochiamata" dalla scheda lead per chi ha una regola "Fissare gli appuntamenti".
  // La regola arriva dopo l'apertura (persone e ruoli si caricano): si applica una volta per apertura e
  // solo se il tipo e' ancora quello del reset (se l'utente l'ha gia' cambiato, resta il suo).
  const typeAppliedRef = useRef<number | null>(null);
  useEffect(() => {
    const reset = resetTypeRef.current;
    if (!session || !reset || reset.seq !== session.seq || !bookingProbe || typeAppliedRef.current === session.seq) return;
    typeAppliedRef.current = session.seq;
    const type = initialActivityType({ defaultType: reset.type, hasBookingRule: true, fromLead: adapter.kind === 'lead' });
    setActivityData((prev) => (prev.type === reset.type ? { ...prev, type } : prev));
  }, [session, bookingProbe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Assegnatario di default = utente corrente quando l'adapter non ne propone uno (fonte :877-884).
  // Con la regola "Fissare gli appuntamenti" decide la regola (sotto): il campo puo' restare vuoto.
  useEffect(() => {
    if (!session || session.editingActivity || !data.currentUserId || booking) return;
    setActivityData((prev) => (prev.assigned_to ? prev : { ...prev, assigned_to: data.currentUserId }));
  }, [session, data.currentUserId, booking]);

  // Regola "Fissare gli appuntamenti" (solo nuove attivita'): quando inizia a valere (apertura o
  // passaggio a un tipo appuntamento) il campo parte dal valore della regola; dopo, si corregge
  // solo se la persona scelta non e' fra quelle ammesse.
  const bookingAppliedRef = useRef<number | null>(null);
  useEffect(() => {
    if (!session || session.editingActivity) return;
    if (!booking) {
      bookingAppliedRef.current = null;
      return;
    }
    if (bookingAppliedRef.current === session.seq) {
      setActivityData((prev) =>
        prev.assigned_to && !booking.allowedIds.has(prev.assigned_to) ? { ...prev, assigned_to: booking.startId } : prev
      );
      return;
    }
    bookingAppliedRef.current = session.seq;
    setActivityData((prev) => ({ ...prev, assigned_to: booking.startId }));
  }, [session, booking]);

  // Sync pre-spuntata e calendario selezionato seguono le impostazioni dell'assegnatario.
  // ECCEZIONE B1: la spunta iniziale è provider-aware (Google **o** Microsoft) tramite
  // `resolveSyncSwitch` — prima guardava solo Google e un assegnatario Microsoft-only non
  // poteva attivare la sync. Le impostazioni Microsoft arrivano dalla STESSA risoluzione
  // già fatta da `syncSettings.resolveUserSyncSettings` (una sola query su `user_profiles`,
  // esposta come `data.settings` da `useDrawerData`): nessuna query nuova.
  useEffect(() => {
    const google = data.settings?.google;
    const microsoft = data.settings?.microsoft;
    setSyncOptIn(
      resolveSyncSwitch(
        { enabled: !!google?.enabled, calendarId: google?.calendarId ?? null },
        { enabled: !!microsoft?.enabled, connected: !!microsoft?.connected, calendarId: microsoft?.calendarId ?? null }
      ).defaultChecked
    );
    setSelectedCalendarId(google?.calendarId || '');
  }, [data.settings]);

  // Provider meeting di default (fonte :510-516).
  useEffect(() => {
    if (activityData.type !== 'videochiamata') {
      setMeetingProvider('none');
      return;
    }
    setMeetingProvider(data.settings?.google.connected ? 'google_meet' : 'none');
  }, [activityData.type, data.settings?.google.connected]);

  // Attendee pre-selezionati (contatti cliente o invitati reali dell'evento Google).
  useEffect(() => {
    if (data.resolvedAttendees) setSelectedAttendees(data.resolvedAttendees);
  }, [data.resolvedAttendees]);

  // L'organizzatore proposto è sempre l'assegnatario corrente (fonte: default 'agent').
  useEffect(() => {
    setOrganizerId(activityData.assigned_to || '');
  }, [activityData.assigned_to]);

  return {
    session,
    booking,
    data,
    editor,
    tab,
    setTab,
    note,
    setNote,
    files,
    setFiles,
    titleError,
    setTitleError,
    activityData,
    setActivityData,
    syncOptIn,
    setSyncOptIn,
    meetingProvider,
    setMeetingProvider,
    selectedAttendees,
    setSelectedAttendees,
    organizerId,
    setOrganizerId,
    selectedCalendarId,
    setSelectedCalendarId,
    includeDescriptionInEvent,
    includeDescriptionKnown,
    setIncludeDescriptionInEvent,
  };
}
